// What the portfolio page shows: one account, "Capital" (all accounts) or a composite portfolio
// (docs/portfolios/SPEC.md, decisions 16–21). Aggregates are built here from account snapshots.
import { toNumber } from './format';

export const CAPITAL_KEY = 'capital';

const TOTAL_CURRENCY = 'RUB';

export function accountView(portfolio) {
  return {
    ...portfolio,
    key: `p:${portfolio.id}`,
    kind: 'account',
    account_ids: [portfolio.account_id],
    members: [{ portfolio_id: portfolio.id, uids: null }],
  };
}

// Payout rows (GET /portfolios/payouts) that belong to a view: its accounts and, for a composite
// portfolio, its chosen assets (docs/payouts/SPEC.md, decision 9).
export function payoutsForView(view, rows) {
  const members = new Map(view.members.map((m) => [m.portfolio_id, m.uids ? new Set(m.uids) : null]));
  return rows.filter((row) => {
    if (!members.has(row.portfolio_id)) {
      return false;
    }
    const uids = members.get(row.portfolio_id);
    return uids === null || uids.has(row.instrument_uid);
  });
}

// fx: today's CBR rates { USD: 83.6, … } from GET /portfolios (docs/fx/SPEC.md, decision 6).
export function capitalView(portfolios, name, fx = {}) {
  return aggregateView({
    fx,
    key: CAPITAL_KEY,
    kind: 'capital',
    name,
    members: portfolios.map((portfolio) => ({ portfolio, uids: null })),
  });
}

export function groupView(group, portfoliosById, fx = {}) {
  const members = group.members
    .map((m) => ({ portfolio: portfoliosById.get(m.portfolio_id), uids: m.instrument_uids }))
    .filter((m) => m.portfolio);
  return { ...aggregateView({ key: `g:${group.id}`, kind: 'group', name: group.name, members, fx }), group };
}

function aggregateView({ key, kind, name, members, fx }) {
  let total = 0;
  let invested = 0;
  let daily = 0;
  let hasDaily = false;
  const parts = [];

  for (const { portfolio, uids } of members) {
    if (uids === null || uids === undefined) {
      // Whole account: the broker's totals (decision 20).
      const amount = toNumber(portfolio.total_amount) ?? 0;
      const percent = toNumber(portfolio.expected_yield_percent);
      total += amount;
      invested += percent === null || percent <= -100 ? amount : amount / (1 + percent / 100);
      if (toNumber(portfolio.daily_yield) !== null) {
        daily += toNumber(portfolio.daily_yield);
        hasDaily = true;
      }
      parts.push(...(portfolio.positions ?? []).map((p) => ({ ...p, outside_total: false })));
      continue;
    }

    // Chosen assets only: totals from those positions, other currencies at the CBR rate;
    // a currency without a rate stays out of the total.
    const wanted = new Set(uids);
    for (const position of portfolio.positions ?? []) {
      if (!wanted.has(position.instrument_uid)) {
        continue;
      }
      const rate = position.currency === TOTAL_CURRENCY ? 1 : toNumber(fx[position.currency]);
      parts.push({ ...position, outside_total: rate === null });
      if (rate === null) {
        continue;
      }
      const qty = toNumber(position.quantity) ?? 0;
      total += (toNumber(position.current_price) ?? 0) * qty * rate;
      invested += (toNumber(position.average_price) ?? 0) * qty * rate;
      if (toNumber(position.daily_yield) !== null) {
        daily += toNumber(position.daily_yield) * rate;
        hasDaily = true;
      }
    }
  }

  const syncedAt = members
    .map((m) => m.portfolio.synced_at)
    .filter(Boolean)
    .sort()[0] ?? null;

  return {
    key,
    kind,
    id: key,
    name,
    account_id: null,
    type: null,
    currency: TOTAL_CURRENCY,
    total_amount: total,
    expected_yield_percent: invested ? (total / invested - 1) * 100 : null,
    daily_yield: hasDaily ? daily : null,
    synced_at: syncedAt,
    account_ids: members.map((m) => m.portfolio.account_id),
    account_count: members.length,
    members: members.map((m) => ({ portfolio_id: m.portfolio.id, uids: m.uids ?? null })),
    positions: mergePositions(parts),
  };
}

// One row per instrument (decision 19): sums, and quantity-weighted prices. A weighted snapshot price
// keeps the live recalculation (live − price) × quantity equal to the sum over the accounts.
function mergePositions(parts) {
  const byUid = new Map();
  for (const part of parts) {
    const uid = part.instrument_uid || `id:${part.id}`;
    if (!byUid.has(uid)) {
      byUid.set(uid, []);
    }
    byUid.get(uid).push(part);
  }

  return [...byUid.entries()].map(([uid, group]) => {
    const first = group[0];
    if (group.length === 1) {
      return { ...first, id: `m:${uid}` };
    }
    return {
      ...first,
      id: `m:${uid}`,
      quantity: sum(group, 'quantity'),
      average_price: weighted(group, 'average_price'),
      average_price_fifo: weighted(group, 'average_price_fifo'),
      current_price: weighted(group, 'current_price'),
      expected_yield: sum(group, 'expected_yield'),
      expected_yield_fifo: sum(group, 'expected_yield_fifo'),
      daily_yield: sum(group, 'daily_yield'),
      blocked: group.some((p) => p.blocked),
      outside_total: group.some((p) => p.outside_total),
    };
  });
}

/**
 * Totals of a view with live prices (docs/live-prices/SPEC.md, decision 10): value, invested by the
 * broker's all-time %, profit and today's change. Shared by the portfolio card and the analytics summary.
 * fxRates: today's CBR rates; a position in another currency without a rate stays on the snapshot.
 */
export function portfolioTotals(portfolio, livePrices = {}, fxRates = {}) {
  const rateOf = (position) => (position.currency === portfolio.currency ? 1 : toNumber(fxRates[position.currency]));
  const liveDelta = (position) => {
    const live = toNumber(livePrices[position.instrument_uid]?.price);
    const snapshot = toNumber(position.current_price);
    const qty = toNumber(position.quantity);
    return live === null || snapshot === null || qty === null ? 0 : (live - snapshot) * qty;
  };

  const delta = (portfolio.positions ?? [])
    .filter((p) => rateOf(p) !== null && !p.outside_total)
    .reduce((s, p) => s + liveDelta(p) * rateOf(p), 0);

  const snapshotTotal = toNumber(portfolio.total_amount);
  const total = snapshotTotal === null ? null : snapshotTotal + delta;

  // invested = total / (1 + p/100) from the broker's all-time %.
  const percent = toNumber(portfolio.expected_yield_percent);
  const invested = snapshotTotal === null || percent === null || percent <= -100
    ? null
    : snapshotTotal / (1 + percent / 100) || null;

  const yieldAbs = invested === null || total === null ? null : total - invested;
  const yieldPercent = invested === null ? percent : (total / invested - 1) * 100;

  // Today = the broker's change in the snapshot + the live price movement since it (docs/portfolios/SPEC.md,
  // decision 24). A snapshot from before today would add up more than one day: no value then.
  const snapshotDaily = toNumber(portfolio.daily_yield);
  const daily = snapshotDaily === null || !syncedToday(portfolio.synced_at) ? null : snapshotDaily + delta;
  // The broker's % while prices have not moved; otherwise against yesterday's value.
  let dailyPercent = delta === 0 ? toNumber(portfolio.daily_yield_percent) : null;
  if (daily === null) {
    dailyPercent = null;
  } else if (dailyPercent === null) {
    const base = snapshotTotal === null ? 0 : snapshotTotal - snapshotDaily;
    dailyPercent = base ? (daily / base) * 100 : null;
  }

  return { total, invested, yieldAbs, yieldPercent, daily, dailyPercent };
}

// Flat 13% for every account until rates per account exist (docs/analytics/SPEC.md, decision 12).
const PASSIVE_TAX_RATE = 0.13;

/**
 * Passive income for a year: dividends and coupons with a known amount among the calendar items of
 * the next 12 months, after tax. Shared by the analytics summary and the payout calendar.
 */
export function passiveIncomeYear(forwardItems) {
  const gross = forwardItems
    .filter((item) => item.kind === 'dividend' || item.kind === 'coupon')
    .reduce((sum, item) => sum + (toNumber(item.amount_rub) ?? 0), 0);
  return gross * (1 - PASSIVE_TAX_RATE);
}

const MOSCOW_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' });

// The snapshot was taken today, Moscow time (the trading day of T-Invest).
export function syncedToday(syncedAt, now = new Date()) {
  return Boolean(syncedAt) && MOSCOW_DATE.format(new Date(syncedAt)) === MOSCOW_DATE.format(now);
}

function sum(group, field) {
  const values = group.map((p) => toNumber(p[field])).filter((v) => v !== null);
  return values.length === 0 ? null : values.reduce((a, b) => a + b, 0);
}

function weighted(group, field) {
  let amount = 0;
  let qty = 0;
  for (const p of group) {
    const value = toNumber(p[field]);
    const q = toNumber(p.quantity);
    if (value === null || !q) {
      // A part without the price would skew the average: no value rather than a wrong one.
      return null;
    }
    amount += value * q;
    qty += q;
  }
  return qty ? amount / qty : null;
}
